// The two rendered halves of bulk selection — the bar and one row's checkbox.
// The state and the write live in `lib/bulkSelection.ts`, split out so this
// file exports components alone (`react-refresh/only-export-components`); read
// that file's header for the whole design and for which lists are deliberately
// left out of it.
import { Button, Checkbox, makeStyles } from '@fluentui/react-components'
import type { BulkSelection } from '../lib/bulkSelection'
import { useDecisionButtonStyles } from '../lib/theme'

const useStyles = makeStyles({
  // ONE ROW, AND ONLY WHEN SOMETHING IS TICKED (owner, 2026-09-15: "I liked it
  // better when our tables were relatively flush underneath the sub-tabs ...
  // hiding the action buttons until rows are actually selected"). It was a
  // permanent 40px strip carrying a labelled Select all and two greyed-out
  // buttons, on every list, on every visit — so the first row of the table,
  // which is what anyone came to read, sat a control's height further down
  // whether or not the control was ever used.
  //
  // `minHeight` is gone with it: the bar has no height when it does not render,
  // and reserving the space would defeat the point.
  bar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: '8px',
    padding: '0 0 12px',
  },
  // The leading checkbox column. Narrow, and neither the header's checkbox nor
  // a row's carries a visible label — the column is 44px wide and a word beside
  // either would not fit it.
  cell: { width: '44px' },
})

/**
 * The bar: Mark Inactive then Mark Active — red before green, the invoice
 * modal's order (owner, 2026-09-29, D-198) — right-aligned, and NOTHING when the
 * selection is empty.
 *
 * Select all moved into the table's own header cell (`BulkSelectAllCheckbox`),
 * where it sits above the column of row checkboxes it controls — so there is
 * no chrome at all between the sub-tabs and the table until somebody ticks
 * something (owner, 2026-09-15).
 *
 * `busy` disables both actions while a write is in flight — the writes are
 * sequential, so a second press mid-run would interleave two sets of patches
 * against the same rows.
 */
export function BulkBar({
  selection,
  busy,
  labels,
  onMark,
}: {
  selection: BulkSelection
  busy?: boolean
  /** What the two actions are called, where a list does not say "Active" and
      "Inactive". Vehicles says **In Service** / **Out of Service** — the
      fleet's own words, because that is the value its bulk write actually sets
      (owner, 2026-09-14). */
  labels?: { active: string; inactive: string }
  onMark: (active: boolean) => void
}) {
  const s = useStyles()
  // THE SHARED DECISION-BUTTON STYLES (owner, 2026-09-14: "why are the buttons
  // green and red when nothing is selected? This is inconsistent with the rest
  // of the app"). They were two hand-written hexes with no `:disabled` arm, so
  // a bar with nothing ticked showed two saturated fills that looked pressable.
  // `useDecisionButtonStyles` is the same pair the queue screens' Approve and
  // Deny already use, and it greys both out when the button is disabled —
  // which is the consistency the owner was asking for and the reason not to
  // hand-roll a third copy.
  const btn = useDecisionButtonStyles()
  const n = selection.count
  const activeLabel = labels?.active ?? 'Active'
  const inactiveLabel = labels?.inactive ?? 'Inactive'
  // NOT `disabled`, which is what the buttons carried while the bar was
  // permanent: with nothing ticked there is no action to offer, and an absent
  // control says that better than a greyed-out one.
  if (n === 0) return null
  return (
    <div className={s.bar}>
      <Button appearance="primary" className={btn.deny} disabled={busy} onClick={() => onMark(false)}>
        {`Mark ${n} ${inactiveLabel}`}
      </Button>
      <Button appearance="primary" className={btn.approve} disabled={busy} onClick={() => onMark(true)}>
        {`Mark ${n} ${activeLabel}`}
      </Button>
    </div>
  )
}

/**
 * The header cell's Select all — unlabelled, in the table's top-left corner
 * (owner, 2026-09-15).
 *
 * `checked="mixed"` is the third state Fluent's Checkbox takes, and it is the
 * honest one for a partial selection: a plain unticked box beside three ticked
 * rows says "nothing is selected", and a ticked one says "everything is".
 * Clicking it from mixed selects the rest, which is the standard behaviour and
 * the one a reader who ticked three rows and wants them all expects.
 */
export function BulkSelectAllCheckbox({
  selection,
  label,
}: {
  selection: BulkSelection
  /** Names the list for a screen reader: "Select all vendors". */
  label: string
}) {
  const checked = selection.allSelected ? true : selection.count > 0 ? 'mixed' : false
  return (
    <Checkbox
      checked={checked}
      onChange={selection.toggleAll}
      aria-label={selection.allSelected ? `Unselect all ${label}` : `Select all ${label}`}
    />
  )
}

/**
 * One row's checkbox, for a table that builds its own rows.
 *
 * `stopPropagation` is the whole reason this is a component rather than a bare
 * `<Checkbox>`: every row on these two screens IS its edit control
 * (`rowActivationProps`), so a click that reached the row would tick the box
 * and open the editor over it.
 */
export function BulkCheckbox({
  selection,
  rowKey,
  label,
}: {
  selection: BulkSelection
  rowKey: string
  label: string
}) {
  return (
    <Checkbox
      checked={selection.has(rowKey)}
      aria-label={`Select ${label}`}
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => e.stopPropagation()}
      onChange={() => selection.toggle(rowKey)}
    />
  )
}

